import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Button, Modal } from 'examkrackers-components'

import CheckRoundedIcon from 'assets/check-rounded.svg'
import SaltyTakingNotes from 'assets/SaltyTakingNotes.svg'
import { EXTEND_SUBSCRIPTION_URL } from 'utils/courses'

export type PracticeMcatsPromoModalProps = {
  open: boolean
  onClose: () => void
}

const BENEFITS = [
  {
    translationKey: 'freeTrial.practiceMcatsPromoModal.benefit1',
    count: 11
  },
  {
    translationKey: 'freeTrial.practiceMcatsPromoModal.benefit2',
    count: 24
  },
  {
    translationKey: 'freeTrial.practiceMcatsPromoModal.benefit3',
    count: 44
  }
] as const

const PracticeMcatsPromoModal = ({
  open,
  onClose
}: PracticeMcatsPromoModalProps): JSX.Element => {
  const { t } = useTranslation()

  const handleViewPlans = () => {
    window.open(EXTEND_SUBSCRIPTION_URL, '_blank', 'noopener,noreferrer')
  }

  const handleBackToFreeTrial = () => {
    onClose()
  }

  return (
    <Modal
      open={open}
      handleClose={onClose}
      hasBorder
      data-testid='PracticeMcatsPromoModal-Modal'
    >
      <MainWrapper data-testid='PracticeMcatsPromoModal-MainWrapper'>
        <TextWrapper data-testid='PracticeMcatsPromoModal-TextWrapper'>
          <Eyebrow data-testid='PracticeMcatsPromoModal-Eyebrow'>
            {t('freeTrial.practiceMcatsPromoModal.eyebrow')}
          </Eyebrow>
          <Title data-testid='PracticeMcatsPromoModal-Title'>
            {t('freeTrial.practiceMcatsPromoModal.title')}
          </Title>
        </TextWrapper>

        <Content data-testid='PracticeMcatsPromoModal-Content'>
          <BenefitsList data-testid='PracticeMcatsPromoModal-BenefitsList'>
            {BENEFITS.map(benefit => (
              <BenefitItem key={benefit.translationKey}>
                <BenefitIcon src={CheckRoundedIcon} alt='' aria-hidden />
                <BenefitLeaders>
                  <BenefitLabel>{t(benefit.translationKey)}</BenefitLabel>
                  <BenefitDots aria-hidden />
                  <BenefitCount>{benefit.count}</BenefitCount>
                </BenefitLeaders>
              </BenefitItem>
            ))}
          </BenefitsList>

          <SaltyContainer data-testid='PracticeMcatsPromoModal-SaltyContainer'>
            <SaltyImage
              data-testid='PracticeMcatsPromoModal-SaltyImage'
              src={SaltyTakingNotes}
              alt=''
              aria-hidden
            />
          </SaltyContainer>
        </Content>

        <ButtonsContainer data-testid='PracticeMcatsPromoModal-ButtonsContainer'>
          <ViewPlansButton
            color='primary'
            onClick={handleViewPlans}
            aria-label={t('freeTrial.practiceMcatsPromoModal.viewPlans')}
            data-testid='PracticeMcatsPromoModal-ViewPlansButton'
          >
            {t('freeTrial.practiceMcatsPromoModal.viewPlans')}
          </ViewPlansButton>
          <BackToFreeTrialLink
            role='button'
            tabIndex={0}
            onClick={handleBackToFreeTrial}
            onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleBackToFreeTrial()
              }
            }}
            aria-label={t('freeTrial.practiceMcatsPromoModal.backToFreeTrial')}
            data-testid='PracticeMcatsPromoModal-BackToFreeTrialLink'
          >
            {t('freeTrial.practiceMcatsPromoModal.backToFreeTrial')}
          </BackToFreeTrialLink>
        </ButtonsContainer>
      </MainWrapper>
    </Modal>
  )
}

export default PracticeMcatsPromoModal

const MainWrapper = styled.div`
  position: relative;
  margin: 0 48px 0px;
  max-width: 650px;
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

const Eyebrow = styled.div`
  font-family: Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: normal;
  color: #1a1a1a;
  margin-bottom: 24px;
`

const Title = styled.div`
  font-family: Arial, sans-serif;
  font-size: 20px;
  font-weight: 700;
  font-style: normal;
  color: #1a1a1a;
  max-width: 520px;
  margin-bottom: 18px;
`

const Content = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 150px;
  position: relative;
  margin-bottom: 18px;
`

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
  max-width: 340px;
`

const BenefitItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const BenefitIcon = styled.img`
  width: 25px;
  height: 25px;
  flex-shrink: 0;
`

const BenefitLeaders = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`

const BenefitLabel = styled.div`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  color: #444444;
  text-align: left;
  white-space: nowrap;
  flex-shrink: 0;
`

const BenefitDots = styled.div`
  flex: 1;
  min-width: 12px;
  height: 1px;
  margin-top: 10px;
  border-bottom: 2px dotted #444444;
`

const BenefitCount = styled.div`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 400;
  font-style: normal;
  color: #444444;
  flex-shrink: 0;
`

const SaltyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const SaltyImage = styled.img`
  height: 148px;
  width: 130px;
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 42px;
  align-items: center;
  justify-content: center;
`

const ViewPlansButton = styled(Button)`
  display: flex;
  width: 126px;
  min-width: 126px;
  padding: 12px;
  justify-content: center;
`

const BackToFreeTrialLink = styled.div`
  font-family: Arial, sans-serif;
  font-size: 16px;
  font-weight: 700;
  font-style: normal;
  color: #412312;
  text-decoration: underline;
  cursor: pointer;
`
